import { useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CopyIcon, GlobeIcon, LockIcon, UsersIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import {
  ArtifactRecipientPicker,
  type ShareRecipient,
} from '@/features/artifacts/ArtifactRecipientPicker.js';
import { sameMembers } from '@/features/chat-share/conversation-audience.js';
import { useWorkspaceSharing } from '@/features/workspace/useWorkspaceSharing.js';
import { offersSharingControls } from '@/features/workspace/workspace-access.js';
import { WorkspaceMemberPicker } from '@/features/workspace/WorkspaceMemberPicker.js';
import { apiErrorAction } from '@/lib/api-error.js';
import {
  activeAutomationShares,
  automationShareUrl,
  automationSharesQueryOptions,
  useCreateAutomationShare,
  type AutomationShareAccessMode,
} from '@/lib/api/automation-shares.js';
import {
  updateAutomationAudience,
  type AutomationVisibility,
} from '@/lib/api/automations.js';
import { groupMemberCountLabel } from '@/lib/api/directory-groups.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { notify } from '@/lib/notify.js';
import { outsideTabLabel } from '@/lib/share-reach.js';
import { titleWhenTruncated } from '@/lib/title-when-truncated.js';
import { cn } from '@/lib/utils.js';

import { AUTOMATION_AUDIENCE_LEVELS } from './automation-audience.js';
import { AutomationShareList } from './AutomationShareList.js';

/** The icons the audience levels draw, resolved from the pure module's names. */
const LEVEL_ICON = {
  lock: LockIcon,
  users: UsersIcon,
  globe: GlobeIcon,
} as const;

/** The two widths an automation share link can open to, and the wording the outside tab shows for each. */
const OUTSIDE_MODES: readonly {
  mode: AutomationShareAccessMode;
  label: string;
  hint: string;
  Icon: typeof LockIcon;
}[] = [
  {
    mode: 'named',
    label: 'Specific people or groups',
    hint: 'Only the colleagues and Entra groups you pick, signed in.',
    Icon: UsersIcon,
  },
  {
    mode: 'public',
    label: 'Anyone with the link',
    hint: 'No sign-in, anywhere on the internet.',
    Icon: GlobeIcon,
  },
];

/**
 * Share a scheduled automation: who in the workspace can see it (an author-managed audience), and a
 * read-only link to its results that reaches beyond the workspace.
 *
 * Structure mirrors `DashboardShareDialog`: a shared workspace gets both halves as tabs, a personal one
 * only the link half (there is nobody else in it to grant workspace access to), and the body mounts only
 * while the dialog is open so each open starts from the caller's current props instead of carrying stale
 * drafts from a previous open.
 */
export function AutomationShareDialog({
  automationId,
  workspaceId,
  conversationId,
  visibility,
  participantIds,
  open,
  onOpenChange,
}: {
  automationId: string;
  workspaceId: string | null;
  /** The automation's dedicated chat, or `null` when it posts into the author's regular chat — in
   *  that case there is nothing to grant workspace access to, so the audience half shows a note. */
  conversationId: string | null;
  visibility: AutomationVisibility;
  participantIds: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const sharing = useWorkspaceSharing(workspaceId);
  const offersAudience = offersSharingControls(sharing);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Share automation</DialogTitle>
          <DialogDescription>
            Choose who in the workspace can see it, or send a link outside it.
            Readers see what it does and its results and cannot run, pause or
            change it. A link shows the results only and keeps working until you
            revoke it, even if you set the automation back to Private.
          </DialogDescription>
        </DialogHeader>

        {open && (
          <ShareBody
            automationId={automationId}
            workspaceId={workspaceId}
            hasDedicatedChat={conversationId !== null}
            offersAudience={offersAudience}
            savedVisibility={visibility}
            savedParticipantIds={participantIds}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Both halves of the dialog. The audience draft is seeded straight from the caller's props — they come
 * from the automation row or detail the Share button lives on, which already carries the server's
 * current audience, so opening this dialog needs no fetch of its own to know what to preselect.
 */
function ShareBody({
  automationId,
  workspaceId,
  hasDedicatedChat,
  offersAudience,
  savedVisibility,
  savedParticipantIds,
  onOpenChange,
}: {
  automationId: string;
  workspaceId: string | null;
  hasDedicatedChat: boolean;
  offersAudience: boolean;
  savedVisibility: AutomationVisibility;
  savedParticipantIds: readonly string[];
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [draftVisibility, setDraftVisibility] =
    useState<AutomationVisibility | null>(null);
  const [draftIds, setDraftIds] = useState<ReadonlySet<string> | null>(null);
  const [saving, setSaving] = useState(false);

  const savedIds = useMemo(
    () => new Set(savedParticipantIds),
    [savedParticipantIds],
  );
  const visibility = draftVisibility ?? savedVisibility;
  const selectedIds = draftIds ?? savedIds;

  function toggleMember(userId: string, selected: boolean): void {
    const next = new Set(selectedIds);
    if (selected) next.add(userId);
    else next.delete(userId);
    setDraftIds(next);
  }

  const pending =
    visibility !== savedVisibility ||
    (visibility === 'group' && !sameMembers(selectedIds, savedIds));
  /**
   * A group of nobody is refused by the service, so the footer disables `Save` before the round
   * trip. The "Unsaved change" note is drawn only alongside an enabled `Save` — a picked `Group`
   * with no members yet is not something the dialog can save, so it is not yet an unsaved change.
   */
  const canSave =
    !saving && !(visibility === 'group' && selectedIds.size === 0);

  /**
   * Both automation caches, because the badge the author just changed is drawn from both: the
   * Scheduled list reads the summaries, and the detail page open behind this dialog reads its own
   * projection.
   */
  async function save(): Promise<void> {
    setSaving(true);
    try {
      await updateAutomationAudience(automationId, {
        visibility,
        participantIds: visibility === 'group' ? [...selectedIds] : undefined,
      });
      await queryClient.invalidateQueries({
        queryKey: ['automation-summaries'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['automation-projection', automationId],
      });
      notify.success('Audience updated');
      onOpenChange(false);
    } catch (reason) {
      notify.error(apiErrorAction(reason, 'Failed to update audience'));
    } finally {
      setSaving(false);
    }
  }

  /** Feeds only the outside tab's live-count label; `AutomationShareList` owns its own list query. */
  const sharesQuery = useQuery(automationSharesQueryOptions(automationId));
  const liveShareCount = activeAutomationShares(sharesQuery.data ?? []).length;

  const outsideContent = <OutsideTab automationId={automationId} />;

  /**
   * No dedicated chat: this is an automation with no chat of its own at all (an insights one), so
   * there is nothing here to grant workspace access to — a chat-authored automation always has a
   * chat, since cti-conversation-service converts the authoring chat into its dedicated chat at
   * first indexing. The outside link never depended on a dedicated chat, so it keeps working; only
   * the audience half becomes a note.
   */
  if (!hasDedicatedChat) {
    return (
      <Tabs defaultValue="inside">
        <TabsList>
          <TabsTrigger value="inside">In this workspace</TabsTrigger>
          <TabsTrigger value="outside">
            {outsideTabLabel(liveShareCount)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inside">
          <p className="text-sm text-muted-foreground">
            This automation has no chat of its own, so it cannot be shared
            inside the workspace. Links still work.
          </p>
        </TabsContent>

        <TabsContent
          value="outside"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          {outsideContent}
        </TabsContent>
      </Tabs>
    );
  }

  /**
   * A personal workspace has nobody else to grant workspace access to, so the audience tab would be a
   * tab onto an empty question. The link half is the whole dialog there, exactly as it would be alone.
   */
  if (!offersAudience) return outsideContent;

  return (
    <>
      <Tabs defaultValue="inside">
        <TabsList>
          <TabsTrigger value="inside">In this workspace</TabsTrigger>
          <TabsTrigger value="outside">
            {outsideTabLabel(liveShareCount)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inside" className="flex flex-col gap-2">
          <div
            className="flex flex-col gap-2"
            role="radiogroup"
            aria-label="Automation visibility"
          >
            {AUTOMATION_AUDIENCE_LEVELS.map((level) => (
              <ReachOption
                key={level.visibility}
                label={level.label}
                hint={level.hint}
                Icon={LEVEL_ICON[level.icon]}
                selected={visibility === level.visibility}
                onSelect={() => setDraftVisibility(level.visibility)}
              />
            ))}
          </div>

          {visibility === 'group' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Members</span>
              <WorkspaceMemberPicker
                workspaceId={workspaceId ?? ''}
                selectedIds={selectedIds}
                onToggle={toggleMember}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="outside"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          {outsideContent}
        </TabsContent>
      </Tabs>

      {pending && (
        <DialogFooter className="sm:items-center">
          {canSave ? (
            <span
              data-testid="automation-share-unsaved"
              className="text-xs text-warning-600 sm:mr-auto"
            >
              Unsaved change to who can see it
            </span>
          ) : (
            <span className="sm:mr-auto" />
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

/**
 * Hand a read-only results link to somebody the workspace does not contain, and list the links already
 * handed out. Its own draft (mode, picked recipients, name, the link just created) lives here rather
 * than in the audience half, so switching tabs cannot lose either half's unsaved work.
 *
 * That promise needs the `forceMount` on its `TabsContent`: Radix unmounts an inactive tab's content,
 * which would throw this state away on every visit to the audience tab. A force-mounted panel is not
 * hidden by Radix (that mode exists for animation libraries), so the panel hides itself through its
 * `data-state` while inactive, and the draft is still here when the author comes back.
 */
function OutsideTab({ automationId }: { automationId: string }): JSX.Element {
  const [accessMode, setAccessMode] =
    useState<AutomationShareAccessMode>('named');
  const [recipients, setRecipients] = useState<readonly ShareRecipient[]>([]);
  const [name, setName] = useState('');
  const [createdShareId, setCreatedShareId] = useState<string | null>(null);
  const create = useCreateAutomationShare();

  const createdUrl =
    createdShareId !== null ? automationShareUrl(createdShareId) : '';
  /** A `named` share addressed to nobody is refused by the service; the button says so up front. */
  const canCreate = accessMode === 'public' || recipients.length > 0;

  /** A link the author revokes from the list below is no longer worth showing or copying up here. */
  function forgetRevokedShare(shareId: string): void {
    setCreatedShareId((current) => (current === shareId ? null : current));
  }

  function removeRecipient(recipient: ShareRecipient): void {
    setRecipients((current) =>
      current.filter(
        (kept) =>
          kept.subjectType !== recipient.subjectType ||
          kept.subjectId !== recipient.subjectId,
      ),
    );
  }

  function onCreate(): void {
    create.mutate(
      {
        automationId,
        accessMode,
        subjects:
          accessMode === 'named'
            ? recipients.map((recipient) => ({
                subjectType: recipient.subjectType,
                subjectId: recipient.subjectId,
              }))
            : undefined,
        name: name.trim() || undefined,
      },
      {
        onSuccess: (share) => {
          setRecipients([]);
          setName('');
          setCreatedShareId(share.id);
        },
      },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div
        className="flex flex-col gap-2"
        role="radiogroup"
        aria-label="Who can open the link"
      >
        {OUTSIDE_MODES.map((option) => (
          <ReachOption
            key={option.mode}
            label={option.label}
            hint={option.hint}
            Icon={option.Icon}
            selected={accessMode === option.mode}
            onSelect={() => setAccessMode(option.mode)}
          />
        ))}
      </div>

      {accessMode === 'named' && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Recipients</span>
          <ArtifactRecipientPicker
            recipients={recipients}
            onPick={(recipient) =>
              setRecipients((current) => [...current, recipient])
            }
          />
          {recipients.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {recipients.map((recipient) => (
                <li
                  key={`${recipient.subjectType}:${recipient.subjectId}`}
                  className="flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs"
                >
                  {recipient.subjectType === 'entra_group' && (
                    <UsersIcon
                      className="size-3 shrink-0 text-muted-foreground"
                      strokeWidth={2}
                    />
                  )}
                  <span
                    className="min-w-0 truncate"
                    onMouseEnter={titleWhenTruncated}
                  >
                    {recipient.label}
                    {recipient.subjectType === 'entra_group' && (
                      <span className="text-muted-foreground">
                        {' · '}
                        {groupMemberCountLabel(recipient.memberCount)}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${recipient.label}`}
                    onClick={() => removeRecipient(recipient)}
                  >
                    <XIcon className="size-3" strokeWidth={2} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {createdUrl !== '' ? (
        <div className="flex min-w-0 items-center gap-2">
          <Input readOnly value={createdUrl} aria-label="Share link" />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Copy share link"
            onClick={() => void copyToClipboard(createdUrl, 'Link copied')}
          >
            <CopyIcon className="size-4" strokeWidth={1.75} />
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="automation-share-name"
            className="text-sm font-medium text-foreground"
          >
            Name{' '}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <Input
            id="automation-share-name"
            value={name}
            maxLength={100}
            placeholder="e.g. Weekly digest"
            onChange={(event) => setName(event.target.value)}
          />
          <Button
            size="sm"
            disabled={!canCreate || create.isPending}
            onClick={onCreate}
            className="self-start"
          >
            {create.isPending ? 'Creating…' : 'Create link'}
          </Button>
        </div>
      )}

      <AutomationShareList
        automationId={automationId}
        onRevoked={forgetRevokedShare}
      />
    </div>
  );
}

/**
 * One selectable reach, drawn the same for the audience levels and the link modes — different
 * questions, but the same kind of question (how far does this go), so the same control.
 */
function ReachOption({
  label,
  hint,
  Icon,
  selected,
  onSelect,
}: {
  label: string;
  hint: string;
  Icon: typeof LockIcon;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:bg-accent',
      )}
    >
      <Icon
        className="size-4 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}
